// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ElementWrapper, SectionContainer, SectionTitle } from "@awc/App/Widgets/DesignSystem/DesignSystem.helpers";
import Search from "@awc/XDL/Icons/Search";
import { createLinkIconStyles } from "@awc/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonIcon, ButtonVariants } from "@insite/mobius/Button";
import FileUpload from "@insite/mobius/FileUpload";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Image from "@insite/mobius/Icons/Image";
import Link from "@insite/mobius/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import capitalize from "lodash/capitalize";
import React, { FC } from "react";
import { css } from "styled-components";

const buttonVariants: { variant: ButtonVariants; label: string }[] = [
    { variant: "primary", label: "primary" },
    { variant: "secondary", label: "Ghost" },
];

interface ButtonsTabStyles {
    gridItem?: GridItemProps;
    smallGridItem?: GridItemProps;
    buttonGridItem?: GridItemProps;
    buttonsWrapper?: InjectableCss;
}

const buttonsTabStyles: ButtonsTabStyles = {
    gridItem: {
        width: 12,
        css: css`
            flex-wrap: wrap;
            padding-bottom: 40px;
        `,
    },
    smallGridItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
            flex-wrap: wrap;
            padding-bottom: 40px;
        `,
    },
    buttonGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            flex-direction: column;
        `,
    },
    buttonsWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            flex-wrap: wrap;
            gap: 12px;
            button {
                width: fit-content;
            }
        `,
    },
};

const styles = buttonsTabStyles;
const ButtonsTab: FC = props => {
    return (
        <SectionContainer>
            {buttonVariants.map(buttonVariant => (
                <GridItem key={buttonVariant.variant} {...styles.buttonGridItem}>
                    <SectionTitle>{`Button: ${capitalize(buttonVariant.label)}`}</SectionTitle>
                    <StyledWrapper {...styles.buttonsWrapper}>
                        <ElementWrapper title="Inactive">
                            <Button sizeVariant="medium" variant={buttonVariant.variant}>
                                {translate("Button : Medium")}
                            </Button>
                        </ElementWrapper>
                        <ElementWrapper
                            title={
                                <>
                                    Disabled
                                    <br />
                                    (Opacity 33%)
                                </>
                            }
                        >
                            <Button disabled sizeVariant="medium" variant={buttonVariant.variant}>
                                {translate("Button : Disabled")}
                            </Button>
                        </ElementWrapper>
                        <ElementWrapper title="small">
                            <Button sizeVariant="small" variant={buttonVariant.variant}>
                                {translate("Button : Small")}
                            </Button>
                        </ElementWrapper>
                        <ElementWrapper title="medium">
                            <Button sizeVariant="medium" variant={buttonVariant.variant}>
                                {translate("Button : Medium")}
                            </Button>
                        </ElementWrapper>
                        <ElementWrapper title="Large">
                            <Button sizeVariant="large" variant={buttonVariant.variant}>
                                {translate("Button: Large")}
                            </Button>
                        </ElementWrapper>
                    </StyledWrapper>
                </GridItem>
            ))}

            <GridItem {...styles.buttonGridItem}>
                <SectionTitle>Button: Special</SectionTitle>
                <StyledWrapper {...styles.buttonsWrapper}>
                    <ElementWrapper title="Inactive">
                        <Button
                            sizeVariant="small"
                            css={css`
                                padding: 0 8px;
                                border: none;
                            `}
                        >
                            <ButtonIcon src={Search} size={16} />
                        </Button>
                    </ElementWrapper>
                    <ElementWrapper
                        title={
                            <>
                                Disabled
                                <br />
                                (Opacity 33%)
                            </>
                        }
                    >
                        <Button
                            sizeVariant="small"
                            css={css`
                                padding: 0 8px;
                                border: none;
                            `}
                            disabled
                        >
                            <ButtonIcon src={Search} size={16} />
                        </Button>
                    </ElementWrapper>
                </StyledWrapper>
            </GridItem>

            <GridItem {...styles.buttonGridItem}>
                <SectionTitle>Icon Links</SectionTitle>
                <StyledWrapper {...styles.buttonsWrapper}>
                    <ElementWrapper title="Inactive">
                        <Link
                            href="/"
                            {...createLinkIconStyles({
                                icon: Image,
                                sizeVariant: "medium",
                                linkStyles: {
                                    icon: {
                                        iconProps: {
                                            size: 12,
                                        },
                                    },
                                },
                            })}
                        >
                            Icon : Left : Large
                        </Link>
                    </ElementWrapper>
                    <ElementWrapper title="Inactive">
                        <Link
                            href="/"
                            {...createLinkIconStyles({
                                icon: Image,
                            })}
                        >
                            Icon : Left : Small
                        </Link>
                    </ElementWrapper>
                    <ElementWrapper title="Inactive">
                        <Link
                            href="/"
                            {...createLinkIconStyles({
                                icon: Image,
                                sizeVariant: "medium",
                                linkStyles: {
                                    icon: {
                                        position: "right",
                                        iconProps: { size: 12 },
                                    },
                                },
                            })}
                        >
                            Icon : Right : Large
                        </Link>
                    </ElementWrapper>
                    <ElementWrapper title="Inactive">
                        <Link
                            href="/"
                            {...createLinkIconStyles({
                                icon: Image,
                                linkStyles: {
                                    icon: {
                                        position: "right",
                                    },
                                },
                            })}
                        >
                            Icon : Right : Small
                        </Link>
                    </ElementWrapper>
                    <ElementWrapper
                        title={
                            <>
                                Disabled
                                <br />
                                (Opacity 33%)
                            </>
                        }
                    >
                        <Link href="/" {...createLinkIconStyles({ icon: Image })} disabled>
                            Text Example
                        </Link>
                    </ElementWrapper>
                </StyledWrapper>
            </GridItem>

            <GridItem {...styles.gridItem}>
                <SectionTitle>{translate("Upload File Button")}</SectionTitle>
                <StyledWrapper {...styles.buttonsWrapper}>
                    <StyledWrapper style={{ maxWidth: "450px" }}>
                        <FileUpload
                            accept=".xls,.xlsx,.csv"
                            label="Upload a File"
                            labelPosition="top"
                            uid="orderUploadFileUpload"
                        />
                    </StyledWrapper>
                </StyledWrapper>
            </GridItem>
        </SectionContainer>
    );
};

export default ButtonsTab;
